// Visual editor for a project's end-user auth/RBAC config (application.auth).
//
// Saved into application.json by the project properties form; consumed by the
// standalone runtime (per-project login gating + object/action grants seeded
// on boot). A "profile" is just a role name.

import React from 'react'
import type { AuthProviderId, ProjectAuthConfig } from '@twasta/page-schema'

const PROVIDERS: { id: AuthProviderId; label: string }[] = [
  { id: 'project-users', label: 'Project users (local signup)' },
  { id: 'twasta', label: 'twasta.ai' },
  { id: 'proteus_vision', label: 'Proteus Vision' },
  { id: 'any', label: 'Any of the above' },
]

const ACTIONS = ['list', 'load', 'add', 'edit', 'save', 'delete', 'item_change', '*']

const inputStyle: React.CSSProperties = {
  padding: '6px 8px', border: '1px solid var(--color-border-strong)', borderRadius: 6,
  fontSize: 14, background: 'var(--color-input-bg)', color: 'var(--color-text)', width: '100%',
}

export function defaultAuthConfig(): ProjectAuthConfig {
  return { providers: ['project-users'], requireLogin: false, defaultRoles: [], grants: [], allowRegistration: false }
}

export function AuthConfigEditor({ value, onChange, objectNames = [], pageNames = [] }: {
  value: ProjectAuthConfig
  onChange: (next: ProjectAuthConfig) => void
  objectNames?: string[]
  /** Smart-page obj_names selectable as the branded login page. */
  pageNames?: string[]
}) {
  // NOTE: preserve every field through the rebuild — `loginPage` /
  // `allowRegistration` were being dropped here, silently wiping them on any
  // edit. Spread `value` first so unknown/forwarded fields survive too.
  const v: ProjectAuthConfig = {
    ...value,
    providers: value?.providers?.length ? value.providers : ['project-users'],
    requireLogin: !!value?.requireLogin,
    defaultRoles: value?.defaultRoles ?? [],
    grants: value?.grants ?? [],
    loginPage: value?.loginPage,
    allowRegistration: !!value?.allowRegistration,
  }
  const set = (patch: Partial<ProjectAuthConfig>) => onChange({ ...v, ...patch })

  const toggleProvider = (id: AuthProviderId) => {
    const has = v.providers.includes(id)
    const next = has ? v.providers.filter((p) => p !== id) : [...v.providers, id]
    set({ providers: next.length ? next : ['project-users'] })
  }

  const grants = v.grants ?? []
  const setGrant = (i: number, patch: Partial<NonNullable<ProjectAuthConfig['grants']>[number]>) =>
    set({ grants: grants.map((g, idx) => (idx === i ? { ...g, ...patch } : g)) })
  const addGrant = () => set({ grants: [...grants, { role: '', obj_name: '', actions: [] }] })
  const removeGrant = (i: number) => set({ grants: grants.filter((_, idx) => idx !== i) })
  const toggleAction = (i: number, a: string) => {
    const cur = grants[i].actions || []
    setGrant(i, { actions: cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a] })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
        <input
          type="checkbox"
          checked={v.requireLogin}
          onChange={(e) => set({ requireLogin: e.target.checked })}
        />
        Require login for internal pages &amp; data (external/public pages stay open)
      </label>

      <div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Sign-in methods</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {PROVIDERS.map((p) => (
            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={v.providers.includes(p.id)} onChange={() => toggleProvider(p.id)} />
              {p.label}
            </label>
          ))}
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
        <input
          type="checkbox"
          checked={!!v.allowRegistration}
          onChange={(e) => set({ allowRegistration: e.target.checked })}
        />
        Allow visitors to self-register (project users)
      </label>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: 4, minWidth: 260 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Two-factor sign-in (authenticator app) for project users
          </span>
          <select
            style={inputStyle}
            value={v.mfa?.mode || 'optional'}
            onChange={(e) => set({ mfa: { ...(v.mfa || {}), mode: e.target.value as 'off' | 'optional' | 'required' } })}
          >
            <option value="off">Off — not offered</option>
            <option value="optional">Optional — each person chooses</option>
            <option value="required">Required for everyone</option>
          </select>
        </label>
        {v.mfa?.mode === 'required' && (
          <label style={{ display: 'grid', gap: 4, width: 170 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Days to set it up</span>
            <input
              style={inputStyle}
              type="number" min={0} max={90}
              value={v.mfa?.grace_days ?? 7}
              onChange={(e) => set({ mfa: { mode: 'required', grace_days: Math.max(0, Math.min(90, Number.parseInt(e.target.value, 10) || 0)) } })}
            />
          </label>
        )}
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: -8 }}>
        Each customer organization&apos;s administrator can make this stricter for their own people (System administration → Sign-in), never looser.
      </div>

      <label style={{ display: 'grid', gap: 4 }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Default profiles for new sign-ups (comma-separated)</span>
        <input
          style={inputStyle}
          value={(v.defaultRoles ?? []).join(', ')}
          onChange={(e) => set({ defaultRoles: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          placeholder="e.g. staff"
        />
      </label>

      <label style={{ display: 'grid', gap: 4 }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Branded login page (a smart page with a sign-in form) — optional
        </span>
        <select
          style={inputStyle}
          value={v.loginPage ?? ''}
          onChange={(e) => set({ loginPage: e.target.value || undefined })}
        >
          <option value="">Built-in login screen</option>
          {pageNames.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Object &amp; action rights (a profile may perform listed actions on an object)
          </span>
          <button type="button" onClick={addGrant} style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}>+ Add</button>
        </div>
        {objectNames.length > 0 && (
          <datalist id="auth-obj-names">{objectNames.map((n) => <option key={n} value={n} />)}</datalist>
        )}
        {/* Reserved roles: `site-visitor` is the public/anonymous external-site
            visitor a page-bound embed resolves to; grant it `action:<name>` to
            expose a transaction's custom action button on a public page. */}
        <datalist id="auth-roles">
          <option value="site-visitor">External website visitors (public/anonymous)</option>
          <option value="admin">Administrators</option>
        </datalist>
        {grants.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
            No grants — every object stays open to any signed-in user. Add a grant to lock one down.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {grants.map((g, i) => (
            <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, display: 'grid', gap: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
                <input style={inputStyle} value={g.role} placeholder="profile (role)" list="auth-roles"
                  onChange={(e) => setGrant(i, { role: e.target.value })} />
                <input style={inputStyle} value={g.obj_name} placeholder="object name" list="auth-obj-names"
                  onChange={(e) => setGrant(i, { obj_name: e.target.value })} />
                <button type="button" onClick={() => removeGrant(i)}
                  style={{ ...inputStyle, width: 'auto', cursor: 'pointer', color: 'var(--color-danger, #dc2626)' }}>Remove</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {ACTIONS.map((a) => (
                  <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <input type="checkbox" checked={(g.actions || []).includes(a)} onChange={() => toggleAction(i, a)} /> {a}
                  </label>
                ))}
              </div>
              {/* Custom transaction action-button rights: `action:<name>` (or
                  `action:*` for all). Required to expose a run_action button to
                  a page-bound anonymous (site-visitor) caller — it is
                  default-deny regardless of the base grants above. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {(g.actions || []).filter((a) => a.startsWith('action:')).map((a) => (
                  <span key={a} style={{ fontSize: 13, background: 'var(--color-bg-subtle, #eef2ff)', borderRadius: 999, padding: '2px 8px', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    {a}
                    <button type="button" onClick={() => toggleAction(i, a)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-danger, #dc2626)', fontSize: 13, lineHeight: 1 }}>×</button>
                  </span>
                ))}
                <input
                  style={{ ...inputStyle, width: 180 }}
                  placeholder="custom action name → Enter"
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    const name = (e.currentTarget.value || '').trim()
                    if (!name) return
                    const entry = name === '*' ? 'action:*' : `action:${name}`
                    if (!(g.actions || []).includes(entry)) toggleAction(i, entry)
                    e.currentTarget.value = ''
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
